import { z } from 'zod'
import { createCandidateSchema } from './candidate'

export const applicationStatuses = ['active', 'hired', 'rejected', 'withdrawn'] as const

export const createApplicationSchema = z.object({
  jobId: z.string().min(1, 'Job ID is required'),
  candidateId: z.string().optional(),
  candidate: createCandidateSchema.optional(),
  coverLetter: z.string().max(50000).optional(),
  answers: z.record(z.unknown()).optional(),
  source: z.string().max(100).optional(),
}).refine(
  (data) => data.candidateId || data.candidate,
  { message: 'Either candidateId or candidate details are required' }
)

export const moveApplicationSchema = z.object({
  stageId: z.string().min(1, 'Stage ID is required'),
  notes: z.string().max(2000).optional(),
})

export const rejectApplicationSchema = z.object({
  reason: z.string().max(2000).optional(),
  sendEmail: z.boolean().default(false),
  emailTemplateId: z.string().optional(),
})

export const applicationFiltersSchema = z.object({
  jobId: z.string().optional(),
  stageId: z.string().optional(),
  status: z.enum(applicationStatuses).optional(),
  search: z.string().max(200).optional(),
  minRating: z.coerce.number().min(1).max(5).optional(),
})

export const createEvaluationSchema = z.object({
  applicationId: z.string().min(1, 'Application ID is required'),
  interviewId: z.string().optional(),
  rating: z.number().int().min(1).max(5).optional(),
  recommendation: z.enum(['strong_yes', 'yes', 'neutral', 'no', 'strong_no']).optional(),
  feedback: z.string().max(10000).optional(),
  skillsAssessment: z.record(z.number().int().min(1).max(5)).optional(),
  isPrivate: z.boolean().default(false),
})

// Public application submission (career page)
export const publicApplicationSchema = z.object({
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  email: z.string().email(),
  phone: z.string().max(50).optional(),
  linkedinUrl: z.string().url().optional().or(z.literal('')),
  portfolioUrl: z.string().url().optional().or(z.literal('')),
  coverLetter: z.string().max(50000).optional(),
  answers: z.record(z.unknown()).optional(),
  gdprConsent: z.boolean().refine((val) => val === true, {
    message: 'You must consent to data processing',
  }),
})

export type CreateApplicationInput = z.infer<typeof createApplicationSchema>
export type MoveApplicationInput = z.infer<typeof moveApplicationSchema>
export type RejectApplicationInput = z.infer<typeof rejectApplicationSchema>
export type ApplicationFiltersInput = z.infer<typeof applicationFiltersSchema>
export type CreateEvaluationInput = z.infer<typeof createEvaluationSchema>
export type PublicApplicationInput = z.infer<typeof publicApplicationSchema>
